import React, { useContext, useEffect, useReducer, useState } from "react";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { default as ReactSelect } from "react-select";
import { toast } from "react-toastify";
import { Store } from "../Store";
import { default as config } from "../config.json";
import useAxios from "../hooks/useAxios";
import useProductsAndCategories from "../hooks/useProductsAndCategories";
import { capitalize, getError } from "../utils";
import Gorsel from "./Gorsel";
import LoadingBox from "./LoadingBox";
import MessageBox from "./MessageBox";
import Option from "./Option";

const reducer = (state, action) => {
  switch (action.type) {
    case "CREATE_REQUEST":
      return { ...state, loadingCreate: true };
    case "CREATE_SUCCESS":
      return {
        ...state,
        loadingCreate: false,
      };
    case "CREATE_FAIL":
      return { ...state, loadingCreate: false };
    case "FETCH_REQUEST":
      return { ...state, loading: true };
    case "FETCH_SUCCESS":
      return { ...state, loading: false };
    case "FETCH_FAIL":
      return { ...state, loading: false, error: action.payload };
    case "UPDATE_REQUEST":
      return { ...state, loadingUpdate: true };
    case "UPDATE_SUCCESS":
      return { ...state, loadingUpdate: false };
    case "UPDATE_FAIL":
      return { ...state, loadingUpdate: false };
    case "UPLOAD_REQUEST":
      return { ...state, loadingUpload: true, errorUpload: "" };
    case "UPLOAD_SUCCESS":
      return {
        ...state,
        loadingUpload: false,
        errorUpload: "",
      };
    case "UPLOAD_FAIL":
      return { ...state, loadingUpload: false, errorUpload: action.payload };

    default:
      return state;
  }
};
export default function CategoryEditor(props) {
  const axios = useAxios();
  const { categoryId, setShowCategoryEditor, handleClose } = props;

  const { state } = useContext(Store);
  const { userInfo, allCategories, categories, products } = state;
  const [
    { loading, error, loadingUpdate, loadingUpload, loadingCreate },
    dispatch,
  ] = useReducer(reducer, {
    loading: false,
    error: "",
    loadingCreate: false,
    loadingUpdate: false,
  });

  const [name, setName] = useState("");
  const [image, setImage] = useState("");
  const [isRoot, setIsRoot] = useState(true);
  const [description, setDescription] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [rootCategoriesList, setRootCategoriesList] = useState([]);

  useEffect(() => {
    setRootCategoriesList(categories.map((c) => c._id));
  }, [categories]);

  const [
    loadingProducts,
    errorLoadingProducts,
    setLoadProducts,
    setLoadCategories,
  ] = useProductsAndCategories();

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: "FETCH_REQUEST" });
        const { data } = await axios.get(`/api/categories/${categoryId}`);
        setName(data.name);
        setImage(data.image);
        setIsRoot(data.isRoot);
        setDescription(data.description);
        setSelectedCategories(data.categories);
        setSelectedProducts(data.products);

        dispatch({ type: "FETCH_SUCCESS" });
      } catch (err) {
        dispatch({
          type: "FETCH_FAIL",
          payload: getError(err),
        });
      }
    };
    if (categoryId && categoryId !== "0") {
      fetchData();
    }
  }, [categoryId]);

  const submitHandler = async (e) => {
    e.preventDefault();
    try {
      dispatch({ type: "UPDATE_REQUEST" });
      categoryId === "0"
        ? await axios.post("/api/categories", {
            name,
            image: image || config.default_image,
            description,
            isRoot,
            categories: selectedCategories,
            products: selectedProducts,
          })
        : await axios.put(`/api/categories/${categoryId}`, {
            _id: categoryId,
            name,
            image,
            description,
            isRoot,
            categories: selectedCategories,
            products: selectedProducts,
          });

      setShowCategoryEditor(false);
      dispatch({
        type: "UPDATE_SUCCESS",
      });
      setLoadProducts(true);
      setLoadCategories(true);
      toast.success("Kategori basarili bir sekilde kaydedildi");
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: "UPDATE_FAIL" });
    }
  };

  return (
    <div>
      {error && <MessageBox variant="danger">{error}</MessageBox>}
      {(loading || loadingUpdate) && <LoadingBox></LoadingBox>}
      <Form onSubmit={submitHandler}>
        <Form.Group className="mb-3">
          <Form.Label>Adı</Form.Label>
          <Form.Control
            value={name}
            onChange={(e) => setName(capitalize(e.target.value))}
            required
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Açıklama</Form.Label>
          <Form.Control
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </Form.Group>

        <Gorsel
          image={image}
          setImage={setImage}
          entityId={categoryId}
          entityName={"category"}
        ></Gorsel>

        <Form.Group className="mb-3">
          <Form.Check
            type="checkbox"
            label="Ana kategori mi?"
            value={isRoot}
            checked={isRoot}
            onChange={(e) => setIsRoot(e.target.checked)}
          />
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Alt Kategoriler</Form.Label>
          <div className="mb-3">
            <ReactSelect
              options={allCategories
                .filter(
                  (c) =>
                    c._id !== categoryId && !rootCategoriesList.includes(c._id)
                )
                .map((c) => {
                  return { value: c._id, label: c.name };
                })}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{
                Option,
              }}
              onChange={(selected) => {
                setSelectedCategories(selected.map((entry) => entry.value));
              }}
              allowSelectAll={true}
              value={allCategories
                .filter((c) => selectedCategories.includes(c._id))
                .map((c) => {
                  return { value: c._id, label: c.name };
                })}
            />
          </div>
        </Form.Group>

        <Form.Group className="mb-3">
          <Form.Label>Ürünler</Form.Label>
          <div className="mb-3">
            <ReactSelect
              options={products.map((p) => {
                return { value: p._id, label: p.name };
              })}
              isMulti
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              components={{
                Option,
              }}
              onChange={(selected) => {
                setSelectedProducts(selected.map((entry) => entry.value));
              }}
              allowSelectAll={true}
              value={products
                .filter((p) => selectedProducts.includes(p._id))
                .map((p) => {
                  return { value: p._id, label: p.name };
                })}
            />
          </div>
        </Form.Group>

        <div className="mb-3 ">
          <Button
            disabled={loadingUpdate}
            type="submit"
            variant="light"
            className="text-primary border-primary me-2"
          >
            {categoryId === "0" ? "Kaydet" : "Güncelle"}
          </Button>
          <Button
            disabled={loadingUpdate}
            variant="light"
            className="text-danger border-danger"
            onClick={handleClose}
          >
            Kapat
          </Button>
        </div>
      </Form>
    </div>
  );
}
